import React, { useEffect, useState } from "react";
import { Trash2, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AdminShell, SaveButton } from "@/components/admin/AdminShell";
import { Text, Area, Toggle } from "@/components/admin/Field";
import { MediaPicker } from "@/components/admin/MediaPicker";
import { useSite } from "@/context/SiteContext";

function Section({ title, children }) {
  return (
    <div className="bg-ivory border border-espresso/10 rounded-md p-7 mb-6">
      <h2 className="font-serif text-2xl text-espresso mb-6">{title}</h2>
      <div className="grid md:grid-cols-2 gap-5">{children}</div>
    </div>
  );
}

export default function SettingsEditor() {
  const [d, setD] = useState(null);
  const [saving, setSaving] = useState(false);
  const { refresh } = useSite();

  useEffect(() => {
    api.get("/content/settings").then((r) => setD(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const setHour = (i, k, v) => setD((p) => { const hours = [...(p.hours || [])]; hours[i] = { ...hours[i], [k]: v }; return { ...p, hours }; });
  const addHour = () => setD((p) => ({ ...p, hours: [...(p.hours || []), { day: "", value: "" }] }));
  const delHour = (i) => setD((p) => ({ ...p, hours: p.hours.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content/settings", d);
      refresh && refresh();
      toast.success("Settings updated across the site");
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!d) return <AdminShell title="Settings"><div className="text-taupe">Loading…</div></AdminShell>;

  return (
    <AdminShell title="Settings" subtitle="Contact, hours, booking & SEO" actions={<SaveButton onClick={save} saving={saving} />}>
      <Section title="Booking">
        <div className="md:col-span-2"><Text label="Jane Booking URL" value={d.booking_url} onChange={(v) => set("booking_url", v)} help="Every Book button on the site opens this link." testid="settings-booking-url" /></div>
        <div className="md:col-span-2"><Text label="Google Review Link" value={d.google_review_url} onChange={(v) => set("google_review_url", v)} help="Every 'Leave a Google review' button opens this link." testid="settings-google-review-url" /></div>
      </Section>

      <div className="bg-ivory border border-espresso/10 rounded-md p-7 mb-6">
        <h2 className="font-serif text-2xl text-espresso mb-2">Brand Logo</h2>
        <p className="text-sm text-taupe mb-6">Shown in the top navigation on every page (including the homepage and Contact). Upload a PNG with a transparent background. Provide a light version for the dark video hero, and a dark version for light pages. If left empty, the elegant "Psalm 30:2" wordmark is used.</p>
        <div className="grid md:grid-cols-2 gap-5">
          <MediaPicker label="Logo — dark (for light backgrounds)" value={d.logo_url} onChange={(v) => set("logo_url", v)} accept="image/png,image/webp,image/jpeg" recommended="Transparent PNG, ~400px tall" testid="settings-logo" />
          <MediaPicker label="Logo — light (for the dark hero)" value={d.logo_light_url} onChange={(v) => set("logo_light_url", v)} accept="image/png,image/webp,image/jpeg" recommended="White/cream transparent PNG" testid="settings-logo-light" />
        </div>
      </div>

      <Section title="Contact Information">
        <Text label="Practice Name" value={d.practice_name} onChange={(v) => set("practice_name", v)} />
        <Text label="Phone" value={d.phone} onChange={(v) => set("phone", v)} testid="settings-phone" />
        <Text label="Email" value={d.email} onChange={(v) => set("email", v)} />
        <div />
        <Text label="Address Line 1" value={d.address_line1} onChange={(v) => set("address_line1", v)} />
        <Text label="Address Line 2" value={d.address_line2} onChange={(v) => set("address_line2", v)} />
        <div className="md:col-span-2"><Text label="Map Search Query" value={d.map_query} onChange={(v) => set("map_query", v)} help="Used for the map & directions link" /></div>
      </Section>

      <div className="bg-ivory border border-espresso/10 rounded-md p-7 mb-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-2xl text-espresso">Hours</h2>
          <button onClick={addHour} className="flex items-center gap-2 text-xs uppercase tracking-[0.14em] border border-espresso/20 rounded-full px-4 py-2 hover:bg-linen"><Plus size={14} /> Add Row</button>
        </div>
        <div className="space-y-3">
          {(d.hours || []).map((h, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-center">
              <input value={h.day} onChange={(e) => setHour(i, "day", e.target.value)} placeholder="Day" className="bg-ivory border border-espresso/20 rounded-md px-3 py-2" />
              <input value={h.value} onChange={(e) => setHour(i, "value", e.target.value)} placeholder="Hours" className="bg-ivory border border-espresso/20 rounded-md px-3 py-2" />
              <button onClick={() => delHour(i)} className="text-destructive/70 hover:text-destructive"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      </div>

      <Section title="Announcement Banner">
        <div className="md:col-span-2"><Area label="Announcement" value={d.announcement} onChange={(v) => set("announcement", v)} rows={2} /></div>
        <Toggle label="Show announcement" value={d.announcement_active} onChange={(v) => set("announcement_active", v)} />
      </Section>

      <Section title="Faith / Scripture">
        <div className="md:col-span-2"><Area label="Scripture Quote" value={d.scripture} onChange={(v) => set("scripture", v)} rows={2} /></div>
        <Text label="Attribution" value={d.scripture_ref} onChange={(v) => set("scripture_ref", v)} />
      </Section>

      <Section title="SEO">
        <div className="md:col-span-2"><Text label="Default SEO Title" value={d.seo_title} onChange={(v) => set("seo_title", v)} /></div>
        <div className="md:col-span-2"><Area label="Default Meta Description" value={d.seo_description} onChange={(v) => set("seo_description", v)} rows={2} /></div>
      </Section>

      <div className="flex justify-end"><SaveButton onClick={save} saving={saving} /></div>
    </AdminShell>
  );
}
